import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VersionHistory = ({ versions, onRestore, onCompare }) => {
  const [selectedVersions, setSelectedVersions] = useState([]);

  const handleVersionSelect = (versionId) => {
    if (selectedVersions?.includes(versionId)) {
      setSelectedVersions(selectedVersions?.filter(id => id !== versionId));
    } else if (selectedVersions?.length < 2) {
      setSelectedVersions([...selectedVersions, versionId]);
    }
  };

  const handleCompare = () => {
    if (selectedVersions?.length === 2) {
      onCompare(selectedVersions?.[0], selectedVersions?.[1]);
    }
  };

  const getVersionTypeColor = (type) => {
    const colors = {
      'auto-save': 'bg-muted text-muted-foreground',
      'manual-save': 'bg-primary/10 text-primary',
      'ai-generation': 'bg-secondary/10 text-secondary',
      'milestone': 'bg-success/10 text-success'
    };
    return colors?.[type] || colors?.['auto-save'];
  };

  const getVersionTypeIcon = (type) => {
    const icons = {
      'auto-save': 'Clock',
      'manual-save': 'Save',
      'ai-generation': 'Sparkles',
      'milestone': 'Flag'
    };
    return icons?.[type] || 'File';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm">
      <div className="p-4 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Version History
        </h3>
        {selectedVersions?.length === 2 && (
          <Button
            variant="default"
            size="sm"
            iconName="GitCompare"
            iconPosition="left"
            onClick={handleCompare}
          >
            Compare Versions
          </Button>
        )}
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {versions?.map((version) => {
          const isSelected = selectedVersions?.includes(version?.id);
          const canSelect = selectedVersions?.length < 2 || isSelected;

          return (
            <div
              key={version?.id}
              className={`p-4 transition-colors duration-200 ${
                isSelected ? 'bg-primary/5' : 'hover:bg-muted/30'
              }`}
            >
              <div className="flex items-start gap-4">
                <button
                  onClick={() => handleVersionSelect(version?.id)}
                  disabled={!canSelect}
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                    isSelected
                      ? 'border-primary bg-primary'
                      : canSelect
                      ? 'border-border hover:border-primary' :'border-muted cursor-not-allowed opacity-50'
                  }`}
                  aria-label="Select version for comparison"
                >
                  {isSelected && (
                    <Icon name="Check" size={12} className="text-primary-foreground" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {version?.name}
                        </h4>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${getVersionTypeColor(version?.type)}`}>
                          <Icon name={getVersionTypeIcon(version?.type)} size={10} />
                          {version?.type?.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Calendar" size={12} />
                          {version?.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          {version?.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="FileText" size={12} />
                          {version?.wordCount?.toLocaleString()} words
                        </span>
                      </div>
                    </div>

                    {!version?.isCurrent && (
                      <Button
                        variant="outline"
                        size="xs"
                        iconName="RotateCcw"
                        iconPosition="left"
                        onClick={() => onRestore(version?.id)}
                      >
                        Restore
                      </Button>
                    )}
                  </div>

                  {version?.description && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {version?.description}
                    </p>
                  )}

                  {version?.changes && version?.changes?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {version?.changes?.map((change, index) => (
                        <div key={index} className="flex items-start gap-2 text-xs">
                          <Icon 
                            name={change?.type === 'added' ? 'Plus' : change?.type === 'removed' ? 'Minus' : 'Edit2'} 
                            size={12} 
                            className={`flex-shrink-0 mt-0.5 ${
                              change?.type === 'added' ? 'text-success' : 
                              change?.type === 'removed'? 'text-error' : 'text-warning'
                            }`}
                          />
                          <span className="text-muted-foreground">{change?.description}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {version?.isCurrent && (
                    <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-md text-xs font-medium">
                      <Icon name="CheckCircle" size={12} />
                      Current Version
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {selectedVersions?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {selectedVersions?.length} version{selectedVersions?.length !== 1 ? 's' : ''} selected
            </span>
            <button
              onClick={() => setSelectedVersions([])}
              className="text-primary hover:underline"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;